/**
 * Hook useManualCalendarEvent
 *
 * Encapsula mutations de eventos manuais do Calendário Global:
 *  - create_manual_calendar_event
 *  - update_manual_calendar_event
 *  - delete_manual_calendar_event (soft delete)
 *
 * As mutations invalidam o cache de `calendar-unified-events`.
 *
 * RELEASE:
 *   2026-08-04 v1.1.0 - Suporte a descrição rich text (p_description_html)
 *   2026-06-24 v1.0.0 - Versão inicial (Fase 3 Calendário Global)
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useManualCalendarEvent');

export interface ManualEventInput {
  calendarId: string;
  title: string;
  /** Descrição em texto puro (fallback / busca) */
  description?: string | null;
  /** Descrição em HTML sanitizado (rich text) */
  descriptionHtml?: string | null;
  location?: string | null;
  startAt: string; // ISO
  endAt: string;   // ISO
  allDay?: boolean;
  timezone?: string;
  attendees?: Array<{ email: string; displayName?: string }>;
  reminders?: Array<{ method: string; minutes: number }>;
  recurrenceRule?: string | null;
}

export interface ManualEventUpdateInput extends Partial<ManualEventInput> {
  eventId: string;
  /**
   * v1.2.0 — Flags de limpeza explícita.
   * A RPC usa COALESCE (envio de NULL = "não alterar"), então apagar um campo
   * exige sinalizar a intenção com estes flags.
   */
  clearDescription?: boolean;
  clearLocation?: boolean;
  clearRecurrence?: boolean;
}


export function useManualCalendarEvent() {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const tenantId = currentTenant?.id ?? null;

  /**
   * Invalida TODOS os caches usados pelo Calendário Global:
   *  - calendar-month-summary          → visão Mês
   *  - calendar-unified-events         → visões Semana/Dia
   *  - calendar-unified-events-infinite→ visão Lista (scroll infinito)
   * Sem isso, criar/editar/excluir só refletia após F5 nas visões Mês e Lista.
   */
  const invalidateCalendarCaches = () => {
    queryClient.invalidateQueries({ queryKey: ['calendar-unified-events'] });
    queryClient.invalidateQueries({ queryKey: ['calendar-unified-events-infinite'] });
    queryClient.invalidateQueries({ queryKey: ['calendar-month-summary'] });
  };


  // ---- CREATE ----
  const createMutation = useMutation({
    mutationFn: async (input: ManualEventInput) => {
      const { data, error } = await supabase.rpc('create_manual_calendar_event', {
        p_calendar_id: input.calendarId,
        p_title: input.title,
        p_start_at: input.startAt,
        p_end_at: input.endAt,
        p_description: input.description ?? null,
        p_description_html: input.descriptionHtml ?? null,
        p_location: input.location ?? null,
        p_all_day: input.allDay ?? false,
        p_timezone: input.timezone ?? 'America/Sao_Paulo',
        p_attendees: (input.attendees ?? []) as unknown as never,
        p_reminders: (input.reminders ?? []) as unknown as never,
        p_recurrence_rule: input.recurrenceRule ?? null,
        p_tenant_hint: tenantId,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      toast.success('Evento criado');
      invalidateCalendarCaches();
    },
    onError: (err: Error) => {
      logger.error('Erro ao criar evento manual', err);
      toast.error(err.message || 'Erro ao criar evento');
    },
  });

  // ---- UPDATE ----
  const updateMutation = useMutation({
    mutationFn: async (input: ManualEventUpdateInput) => {
      const { data, error } = await supabase.rpc('update_manual_calendar_event', {
        p_event_id: input.eventId,
        p_title: input.title ?? null,
        p_description: input.description ?? null,
        p_description_html: input.descriptionHtml ?? null,
        p_location: input.location ?? null,
        p_start_at: input.startAt ?? null,
        p_end_at: input.endAt ?? null,
        p_all_day: input.allDay ?? null,
        p_timezone: input.timezone ?? null,
        p_attendees: (input.attendees ?? null) as unknown as never,
        p_reminders: (input.reminders ?? null) as unknown as never,
        p_recurrence_rule: input.recurrenceRule ?? null,
        // v1.2.0 — permite realmente apagar descrição / local / recorrência
        p_clear_description: input.clearDescription ?? false,
        p_clear_location: input.clearLocation ?? false,
        p_clear_recurrence: input.clearRecurrence ?? false,
        p_tenant_hint: tenantId,

      });
      if (error) throw error;
      return data as boolean;
    },
    onSuccess: () => {
      toast.success('Evento atualizado');
      invalidateCalendarCaches();
    },
    onError: (err: Error) => {
      logger.error('Erro ao atualizar evento manual', err);
      toast.error(err.message || 'Erro ao atualizar evento');
    },
  });

  // ---- DELETE ----
  const deleteMutation = useMutation({
    mutationFn: async (eventId: string) => {
      const { data, error } = await supabase.rpc('delete_manual_calendar_event', {
        p_event_id: eventId,
        p_tenant_hint: tenantId,
      });
      if (error) throw error;
      return data as boolean;
    },
    onSuccess: () => {
      toast.success('Evento removido');
      invalidateCalendarCaches();
    },
    onError: (err: Error) => {
      logger.error('Erro ao remover evento manual', err);
      toast.error(err.message || 'Erro ao remover evento');
    },
  });

  return {
    createEvent: createMutation.mutate,
    createEventAsync: createMutation.mutateAsync,
    isCreating: createMutation.isPending,

    updateEvent: updateMutation.mutate,
    updateEventAsync: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,

    deleteEvent: deleteMutation.mutate,
    deleteEventAsync: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
}
